import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './stores/authStore';
import { canManageUsers, multiOrgHomePath } from './lib/permissions';

// Import pages
import OrganizationAdminDashboard from './pages/admin/OrganizationAdminDashboard';
import ImpersonationBanner from './components/ui/ImpersonationBanner';
// Code-split entry points: the login page must not pay for the whole app, and the admin shells
// load their own page chunks in turn.
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const SuperAdminDashboard = React.lazy(() => import('./pages/admin/SuperAdminDashboard'));
const AgencyDashboard = React.lazy(() => import('./pages/agency/AgencyDashboard'));
const ScoutDashboard = React.lazy(() => import('./pages/scout/ScoutDashboard'));
const ModelSelfUpdate = React.lazy(() => import('./pages/model/ModelSelfUpdate'));
const ModelDashboardLayout = React.lazy(() => import('./pages/model/ModelDashboardLayout'));
const AgencyModelCompletionPage = React.lazy(() => import('./pages/public/AgencyModelCompletionPage'));
const ClientAdminDashboardLayout = React.lazy(() => import('./pages/admin/ClientAdminDashboardLayout'));
const AgencyBulkCompletionPage = React.lazy(() => import('./pages/public/AgencyBulkCompletionPage'));
const PublicModelBookPage = React.lazy(() => import('./pages/public/PublicModelBookPage'));
const PublicCastingPage = React.lazy(() => import('./pages/public/PublicCastingPage'));
const PublicProductionPage = React.lazy(() => import('./pages/public/PublicProductionPage'));
const PublicBookPackagePage = React.lazy(() => import('./pages/public/PublicBookPackagePage'));
const PublicConfirmationPage = React.lazy(() => import('./pages/public/PublicConfirmationPage'));
const PublicRequestResponsePage = React.lazy(() => import('./pages/public/PublicRequestResponsePage'));
const PublicUploadRequestPage = React.lazy(() => import('./pages/public/PublicUploadRequestPage'));
const OrgSlugShell = React.lazy(() => import('./pages/admin/OrgSlugShell'));
const AllOrgsShell = React.lazy(() => import('./pages/admin/AllOrgsShell'));
const UnauthorizedPage = React.lazy(() => import('./pages/UnauthorizedPage'));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({ 
  children, 
  allowedRoles 
}) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

// Entry for the bare /organization-admin mount. Real org admins get the dashboard;
// multi_org_admin/super_admin/admin have no org context here (X-Organization-Id is
// only set on the /:orgSlug and /all mounts — see orgContextStore.currentOrgHeader),
// so send them to their multi-org home rather than a context-less, empty dashboard.
//
// Users is the one section that is NOT org-scoped: /api/admin/users* narrows to the
// actor's organizations server-side, so it needs no X-Organization-Id and lives on the
// multi-org (/all) and super-admin (/admin) mounts. Forwarding it — with the rest of the
// path kept — is what makes /organization-admin/users/create reachable again, while every
// genuinely org-scoped path still falls through to the redirect the guard exists for.
const USERS_PREFIX = '/organization-admin/users';
// Models are shared across organizations and live on the multi-org mount too (/all/models,
// silently scoped by DefaultOrgScope), so a deep link such as /organization-admin/models?model=<id>
// is forwarded there with path and query intact rather than dropped on the /all landing page.
const MODELS_PREFIX = '/organization-admin/models';

const startsWithPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const OrganizationAdminEntry: React.FC = () => {
  const { user } = useAuthStore();
  const { pathname, search } = useLocation();
  if (user && user.role !== 'organization_admin') {
    if (startsWithPrefix(pathname, USERS_PREFIX) && canManageUsers(user.role)) {
      const base = user.role === 'super_admin' || user.role === 'admin' ? '/admin/users' : '/all/users';
      return <Navigate to={`${base}${pathname.slice(USERS_PREFIX.length)}${search}`} replace />;
    }
    if (startsWithPrefix(pathname, MODELS_PREFIX)) {
      return <Navigate to={`/all/models${pathname.slice(MODELS_PREFIX.length)}${search}`} replace />;
    }
    return <Navigate to={multiOrgHomePath(user.role)} replace />;
  }
  return <OrganizationAdminDashboard />;
};

// Role-based Dashboard Redirect
const DashboardRedirect: React.FC = () => {
  const { user, isAuthenticated, clearAuth } = useAuthStore();
  
  console.log('DashboardRedirect - isAuthenticated:', isAuthenticated, 'user:', user);
  console.log('localStorage token:', localStorage.getItem('token'));
  console.log('localStorage auth-storage:', localStorage.getItem('auth-storage'));
  
  // Fix inconsistent state
  if (isAuthenticated && !user) {
    console.log('Fixing inconsistent auth state');
    clearAuth();
    return <Navigate to="/login" replace />;
  }
  
  if (!isAuthenticated || !user) {
    console.log('Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('User role:', user.role, 'Redirecting to dashboard');
  
  switch (user.role) {
    case 'super_admin':
    case 'admin':
      return <Navigate to="/admin" replace />;
    case 'organization_admin':
      return <Navigate to="/organization-admin" replace />;
    // The read-only multi-org roles have no home org either, so the same /all shell —
    // but finance_admin opens straight on billing instead of the cross-org dashboard,
    // which is the only screen it is there for. multiOrgHomePath decides, so the shell's
    // logo and catch-all agree with this redirect.
    case 'multi_org_admin':
    case 'multi_org_viewer':
    case 'finance_admin':
      return <Navigate to={multiOrgHomePath(user.role)} replace />;
    case 'pool_admin':
      return <Navigate to="/client-admin" replace />;
    case 'agency':
      return <Navigate to="/agency" replace />;
    case 'scout':
      return <Navigate to="/scout" replace />;
    case 'model':
      return <Navigate to="/model" replace />;
    default:
      console.log('Unknown role, redirecting to login');
      return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <ImpersonationBanner />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
        
        <Suspense fallback={<div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/complete-model/:token" element={<AgencyModelCompletionPage />} />
          <Route path="/complete-agency/bulk/:scoutingId" element={<AgencyBulkCompletionPage />} />
          <Route path="/model-book/shared/:shareId" element={<PublicModelBookPage />} />
          <Route path="/casting/shared/:shareId" element={<PublicCastingPage />} />
          <Route path="/production/shared/:token" element={<PublicProductionPage />} />
          <Route path="/book-package/shared/:token" element={<PublicBookPackagePage />} />
          <Route path="/confirmation/shared/:token" element={<PublicConfirmationPage />} />
          <Route path="/request/shared/:token" element={<PublicRequestResponsePage />} />
          <Route path="/upload-request/:token" element={<PublicUploadRequestPage />} />
          
          {/* Dashboard Redirect */}
          <Route path="/" element={<DashboardRedirect />} />
          
          {/* Protected Super Admin Routes */}
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Organization Admin Routes */}
          <Route 
            path="/organization-admin/*"
            element={
              <ProtectedRoute allowedRoles={['organization_admin', 'multi_org_admin', 'multi_org_viewer', 'finance_admin', 'super_admin', 'admin']}>
                <OrganizationAdminEntry />
              </ProtectedRoute>
            }
          />
          
          {/* Protected Pool Admin Routes */}
          <Route 
            path="/client-admin/*" 
            element={
              <ProtectedRoute allowedRoles={['pool_admin', 'admin']}>
                <ClientAdminDashboardLayout />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Agency Routes */}
          <Route 
            path="/agency/*" 
            element={
              <ProtectedRoute allowedRoles={['agency', 'admin']}>
                <AgencyDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Scout Routes */}
          <Route 
            path="/scout/*" 
            element={
              <ProtectedRoute allowedRoles={['scout', 'admin']}>
                <ScoutDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Model Routes */}
          <Route 
            path="/model/*" 
            element={
              <ProtectedRoute allowedRoles={['model', 'admin']}>
                <ModelDashboardLayout />
              </ProtectedRoute>
            } 
          />
          
          {/* Model Self-Update (Token-based, must come after /model/*) */}
          <Route path="/model-update/:token" element={<ModelSelfUpdate />} />

          {/* /all — multi-org home: merged productions/castings/call-times with an
              org filter; billing/agencies/clients as per-org tabs. */}
          <Route
            path="/all/*"
            element={
              <ProtectedRoute allowedRoles={['multi_org_admin', 'multi_org_viewer', 'finance_admin', 'super_admin', 'admin']}>
                <AllOrgsShell />
              </ProtectedRoute>
            }
          />

          {/* Org-scoped URLs (/puma/productions). Static routes above always win the
              route ranking, so this only catches org slugs. Access is re-checked
              server-side via /organizations/by-slug. */}
          <Route
            path="/:orgSlug/*"
            element={
              <ProtectedRoute allowedRoles={['organization_admin', 'multi_org_admin', 'multi_org_viewer', 'finance_admin', 'super_admin', 'admin']}>
                <OrgSlugShell />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
